/**
 * Keeping the Sales Console's session list current.
 *
 * The console polls: every CONSOLE_REFRESH_INTERVAL_MS it re-reads the
 * session list, so a client who has just typed in their organisation and name
 * shows up without anyone reloading the browser. Only the data is refreshed;
 * the page, its filters and whatever the operator is doing stay as they are.
 *
 * Free of React and of the API client so the timing rules can be tested with
 * `node --test`.
 */

export const CONSOLE_REFRESH_INTERVAL_MS = 10_000;

/**
 * Run `task` now, then again `intervalMs` after each run FINISHES.
 *
 * Scheduling from the end of a run, not on a fixed clock, is what keeps
 * requests from overlapping: a slow refresh delays the next one instead of
 * running alongside it. There is at most one timer at any moment.
 *
 * `refresh()` runs it now (the manual "Try again" / after creating a link).
 * Called while a run is in flight, it runs once more straight after rather
 * than starting a second request. `stop()` cancels the timer; nothing is
 * started after it, and a run already in flight schedules nothing.
 *
 * A task that throws is treated like one that returned: polling continues.
 */
export function createPoller(
  task,
  { intervalMs = CONSOLE_REFRESH_INTERVAL_MS, setTimer = setTimeout, clearTimer = clearTimeout } = {},
) {
  let timer = null;
  let running = false;
  let again = false;
  let stopped = true;

  const cancelTimer = () => {
    if (timer !== null) clearTimer(timer);
    timer = null;
  };

  async function run() {
    if (stopped) return;
    if (running) {
      again = true;
      return;
    }
    cancelTimer();
    running = true;
    try {
      await task();
    } catch {
      // The task reports its own failures; the loop just carries on.
    } finally {
      running = false;
    }
    if (stopped) return;
    if (again) {
      again = false;
      await run();
      return;
    }
    timer = setTimer(() => {
      timer = null;
      run();
    }, intervalMs);
  }

  return {
    start() {
      if (!stopped) return;
      stopped = false;
      run();
    },
    refresh() {
      return run();
    },
    stop() {
      stopped = true;
      again = false;
      cancelTimer();
    },
    get running() {
      return running;
    },
  };
}

/** What, in the session list, means the discovery details are worth re-reading. */
export function sessionSignature(session) {
  return [
    session.lifecycle,
    session.current_layer,
    session.organization_name,
    session.client_name,
    session.completed_at,
    session.report_generated,
  ].join('|');
}

/**
 * The sessions whose details are out of date after a refresh, and the new
 * signatures to compare against next time. A session seen for the first time
 * is not "changed": it has no details yet and is fetched anyway.
 */
export function changedSessions(previous, sessions) {
  const signatures = {};
  const changed = [];
  for (const session of sessions) {
    const id = session.session_id;
    const signature = sessionSignature(session);
    signatures[id] = signature;
    if (id in previous && previous[id] !== signature) changed.push(id);
  }
  return { changed, signatures };
}
